import React, { useContext, useEffect, useRef, useState } from 'react';
import {
    BehaviorSubject,
    EMPTY,
    filter,
    ignoreElements,
    map,
    merge,
    mergeMap,
    Observable,
    of,
    pairwise,
    race,
    share,
    startWith,
    Subject,
    switchMap,
    take,
    tap,
    timer,
    withLatestFrom,
} from 'rxjs';
import { Action, Location } from 'history';
import { useDispatch } from 'react-redux';
import { BlogPostLoading } from 'src/components/Blog/BlogPostLoading';
import {
    BundlePageSkeleton,
    CategoryRootSkeleton,
    CustomizationSkeleton,
    HomeMainWidgetsSkeleton,
    PageTransitionSkeleton,
    ProductPageSkeleton,
} from 'src/elements/Skeletons/Skeletons';
import { Container, Wrapper } from 'src/components/Layout';

import { ResolvedComponent, ResolvedUrl } from '../utils/resolve';
import { createDebug } from '../utils/runtimeDebug';
import { scrollToTopEffect, scrollToTopOffsetEffect } from '../runtime/effects/windowScrollTop';

import { AsyncRouterContext, HistoryEvent } from './AsyncRouter';
import { Msg } from './router.register';

const debug = createDebug('AsyncRouter:AsyncLoader');

/** Prefer each route's existing skeleton during SPA resolve; generic only as fallback. */
function RouteLoadingSkeleton({ componentName }: { componentName?: string }) {
    switch (componentName) {
        case 'ConfigurableProduct':
        case 'Product':
        case 'SimpleProduct':
            return (
                <Wrapper variant="page-inner">
                    <Container variant="mobile-medium-padding">
                        <ProductPageSkeleton />
                    </Container>
                </Wrapper>
            );
        case 'BundleProduct':
        case 'WearerBundleProduct':
            return <BundlePageSkeleton />;
        case 'Home':
        case 'CategoryLandingPage':
            return <HomeMainWidgetsSkeleton />;
        case 'Category':
        case 'CategoryPersonalised':
        case 'CategoryBrandPage':
        case 'CatalogSearch':
            return (
                <Wrapper element="section" variant="widget-wrapper">
                    <Container>
                        <CategoryRootSkeleton />
                    </Container>
                </Wrapper>
            );
        case 'Customisation':
            return (
                <div style={{ padding: '20px 16px' }}>
                    <CustomizationSkeleton />
                </div>
            );
        case 'Blog':
            return <BlogPostLoading />;
        default:
            return <PageTransitionSkeleton />;
    }
}

interface AsyncLoaderProps {
    nav$: Observable<HistoryEvent>;
    complete$: Subject<any>;
    initial: ResolvedComponent | null;
    initialLocation: Location;
    initialAction: Action;
    onlineOutdated$: Observable<{ online: boolean; outdated: boolean }>;
    resolveLocal(
        pathname: string,
        online$: Observable<boolean>,
        outdated$: Observable<boolean>,
    ): Observable<ResolvedComponent>;
}

export const AsyncLoader: React.FC<AsyncLoaderProps> = React.memo((props) => {
    const [resolved, setComponent] = useState<ResolvedComponent | null>(props.initial);
    const [loading, setLoading] = useState(false);
    /** Destination component name once urlResolver finishes (avoids using the previous page's skeleton). */
    const [pendingComponentName, setPendingComponentName] = useState<string | undefined>(
        () => props.initial?.componentName || undefined,
    );
    const resolvedRef = useRef(resolved);
    resolvedRef.current = resolved;
    const { resolveLocal } = props;
    const dispatch = useDispatch();
    const { base$ } = useContext(AsyncRouterContext);

    const initialHistoryEvent = useRef<HistoryEvent>({
        location: props.initialLocation,
        action: props.initialAction,
    });

    useEffect(() => {
        const input: Observable<[[HistoryEvent, HistoryEvent], any]> = props.nav$.pipe(
            tap((x) => debug('incoming history event', x.action, x.location.pathname)),
            tap((x) => dispatch(Msg('AsyncRouter.CHANGE', x))),
            startWith(initialHistoryEvent.current),
            pairwise(),
            withLatestFrom(base$?.current || (new BehaviorSubject<Set<string>>(new Set([])) as any)),
            share(),
        );

        const initialResolvedUrl: ResolvedUrl | null = props.initial && {
            componentName: props.initial.componentName,
            id: props.initial.id,
            urlKey: props.initial.urlKey,
        };

        const componentStream$ = new BehaviorSubject<ResolvedUrl | null>(initialResolvedUrl);

        const differentBase = input.pipe(
            filter((x) => baseFilter(x)),
            switchMap(([[, next]]) => {
                /**
                 * Navigation timing strategy:
                 *
                 * - Resolution that completes within 150ms doesn't flash a
                 *   loading state at all (the user perceives an instant
                 *   page swap, no fade).
                 * - Resolution that takes longer flips `loading` so the
                 *   AsyncLoader wrapper fades out; the fade-in happens when
                 *   the component renders.
                 *
                 * Previously this was a `zip(resolveLocal, timer(500))` so
                 * EVERY navigation paid a hard 500ms tax even when the
                 * component was already cached — which made the dev build
                 * feel much slower than production. We now resolve as fast
                 * as possible and only show the loading state if the work
                 * actually takes a moment.
                 */
                const resolved$ = resolveLocal(
                    next.location.pathname,
                    props.onlineOutdated$.pipe(map((o) => o?.online)),
                    props.onlineOutdated$.pipe(map((o) => o?.outdated)),
                ).pipe(share());

                const showLoadingIfSlow$ = race(timer(150), resolved$).pipe(
                    take(1),
                    tap((v) => {
                        if (typeof v === 'number') {
                            // Clear only when leaving a mounted page. On hard refresh Cmp is
                            // often null while componentName is already known — keep it so we
                            // show ProductPageSkeleton (etc.) instead of the generic one.
                            setPendingComponentName((prev) => (resolvedRef.current?.Cmp ? undefined : prev));
                            setLoading(true);
                        }
                    }),
                    ignoreElements(),
                );

                return merge(showLoadingIfSlow$, resolved$).pipe(
                    mergeMap((resolvedComponent) => {
                        if (resolvedComponent.redirectUrl && resolvedComponent.redirectStatusCode) {
                            debug('redirecting', resolvedComponent);
                            window.location.replace(resolvedComponent.redirectUrl);
                            return EMPTY;
                        }
                        return of([next, resolvedComponent] as const);
                    }),
                );
            }),
            tap(([, resolvedComponent]) => debug('resolved component', resolvedComponent)),
            tap(([, resolvedComponent]) => {
                setPendingComponentName(resolvedComponent.componentName);
                setComponent(resolvedComponent);
            }),
            tap(([, resolvedComponent]) =>
                componentStream$.next({
                    componentName: resolvedComponent.componentName,
                    urlKey: resolvedComponent.urlKey,
                    id: resolvedComponent.id,
                }),
            ),
            tap(([next]) => maybeScrollTop(next)),
        );

        /**
         * Convert same-base navigation events into ones
         * that can still be tracked
         */
        const sameBase = input.pipe(
            filter((x) => !baseFilter(x)),
            tap(([[, next]]) => maybeScrollTop(next)),
            withLatestFrom(componentStream$),
            tap((x) => {
                const [[, next]] = x[0];
                if (x[1]) {
                    const { componentName, id } = x[1];
                    const nextC = {
                        componentName,
                        id,
                        urlKey: next.location.pathname,
                    };
                    componentStream$.next(nextC);
                }
            }),
        );

        /**
         *
         */
        const routerResolved$ = merge(differentBase, sameBase).pipe(
            tap(() => dispatch(Msg('AsyncRouter.Resolved'))),
            share(),
        );

        /**
         * These are events that mark when the current page is 'resolved'
         */
        const componentReady$ = props.complete$.pipe(
            withLatestFrom(routerResolved$, componentStream$),
            tap(([, , cmp]) => {
                if (cmp) {
                    dispatch(Msg('AsyncRouter.Complete', cmp));
                }
                setLoading(false);
                setPendingComponentName(undefined);
            }),
        );

        const subs = merge(routerResolved$, componentReady$).subscribe();

        return () => {
            subs.unsubscribe();
        };
    }, [
        props.complete$,
        props.initial,
        props.nav$,
        initialHistoryEvent,
        setComponent,
        dispatch,
        resolveLocal,
        base$,
        props.onlineOutdated$,
    ]);

    const awaitingRootChunk = Boolean(resolved?.componentName && !resolved?.Cmp);
    const showRouteSkeleton = loading || awaitingRootChunk;
    const skeletonComponentName = pendingComponentName || resolved?.componentName || undefined;

    return (
        <div style={{ position: 'relative' }}>
            {showRouteSkeleton && <RouteLoadingSkeleton componentName={skeletonComponentName} />}
            {/*
             * Keep the route mounted while `loading` so useSetResolved can fire,
             * but clip it out of view instead of opacity-0 (which used min-height:100vh
             * and left an empty gray main).
             */}
            <div
                style={
                    showRouteSkeleton
                        ? { position: 'absolute', width: '100%', height: 0, overflow: 'hidden' }
                        : undefined
                }
                aria-hidden={showRouteSkeleton || undefined}
            >
                {resolved && resolved.Cmp && (
                    <resolved.Cmp id={resolved.id} key={resolved.urlKey} pathname={resolved.urlKey} />
                )}
            </div>
        </div>
    );
});

function baseFilter([[prev, next], excludeSet]) {
    const p1 = prev.location.pathname;
    const p2 = next.location.pathname;
    const [seg1] = p1.slice(1).split('/');
    if (isSameBase([prev, next]) && (excludeSet as Set<string>).has(seg1)) {
        debug('BAILING (BASE) since bases are the same');
        return false;
    }
    if (p1 === p2) {
        debug('BAILING since paths match');
        return false;
    }
    return true;
}

function isSameBase([prev, next]: [HistoryEvent, HistoryEvent]): boolean {
    const p1 = prev.location.pathname;
    const p2 = next.location.pathname;
    const [seg1] = p1.slice(1).split('/');
    const [seg2] = p2.slice(1).split('/');
    return seg1 === seg2;
}

function maybeScrollTop(next: HistoryEvent): undefined | void {
    if (next.action === 'REPLACE') {
        return undefined;
    }

    /**
     * did this location event have a scroll param?
     */
    const withScroll = hasScrollParam(next.location.state);
    if (withScroll) {
        const { offset = 0, scrollTo } = withScroll;
        if (offset) {
            return scrollToTopOffsetEffect(300, scrollTo, offset);
        } else {
            return scrollToTopEffect(300, scrollTo);
        }
    }

    /**
     * Fallback is to just scroll to top immediately
     */
    window.scrollTo(0, 0);
}

function hasScrollParam(input: any): { scrollTo: string; offset?: number } | undefined {
    if (input && typeof input.scrollTo === 'string') {
        return input;
    }
    return undefined;
}
